import { useField } from "formik";
import React, { useMemo } from "react";

import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { RuleGroupType } from "@common/types/transactionRule";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useEntityIdsWithAccrualLedger } from "@api/ledger.api";
import { Menu } from "@design-system/Menu/Menu.lib";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { useWorkspaceRuleGroupsList } from "@api/rules.api";
import { Picker } from "@design-system/Picker/Picker.lib";
import {
    AccountingRuleFormActions,
    BusinessRuleFormActions,
    FormRuleActionType,
    TransactionRuleActionFormSchema,
    TransactionRuleConditionFormSchema,
} from "../constants";
import {
    getAccountTargetEntityIds,
    getEffectiveEntityIds,
} from "../entityScope";
import styles from "../RuleForm.module.scss";
import {
    AccrualAccountActionForm,
    CashAccountActionForm,
} from "./AccountActionForm";
import { CategoryActionForm } from "./CategoryActionForm";
import { ClassActionForm } from "./ClassActionForm";
import { CounterpartyActionForm } from "./CounterpartyActionForm";
import { CustomerActionForm } from "./CustomerActionForm";
import { EntityActionForm } from "./EntityActionForm";
import { MemoActionForm } from "./MemoActionForm";
import { ScheduleActionForm } from "./ScheduleActionForm";
import { getRequiredScheduleEntityIds } from "./scheduleRuleEligibility";

interface Props {
    index: number;
    onRemove: (index: number) => void;
    /**
     * How the action type label ("Update category", ...) is presented.
     * "select" (default) renders the existing `Select.TriggerButton` dropdown;
     * "plain" renders the label as inline gray text for compact layouts.
     */
    labelStyle?: "select" | "plain";
    labelClassName?: string;
    controlButtonSize?: TriggerButtonProps["size"];
    scopedEntityIds?: number[];
}

export const RuleActionForm: React.FC<Props> = ({
    index,
    onRemove,
    labelStyle = "select",
    labelClassName,
    controlButtonSize,
    scopedEntityIds,
}) => {
    const { hasAccess: hasClassesAccess, isFetched: accessFetched } =
        useAllowFeature(PREMIUM_FEATURES.CLASSES);
    const { schedules } = useTypedFlags();
    const { enabled: isGlFirst } = useIsGlFirst();
    const ruleGroupsList = useWorkspaceRuleGroupsList();
    const [{ value: groupId }] = useField<string | undefined>("groupId");
    const [{ value: allActions }] =
        useField<TransactionRuleActionFormSchema[]>("actions");
    const [actionTypeField, , actionTypeHelpers] = useField<FormRuleActionType>(
        `actions.${index}.actionType`,
    );
    const [, , actionValueHelpers] = useField<
        TransactionRuleActionFormSchema["value"]
    >(`actions.${index}.value`);
    const [{ value: allConditions }] =
        useField<TransactionRuleConditionFormSchema[]>("conditions");
    const canRemove = allActions.length > 1;
    const actionType = actionTypeField.value;

    const effectiveEntityIds = useMemo(
        () =>
            getEffectiveEntityIds({
                conditions: allConditions,
                scopedEntityIds,
            }),
        [allConditions, scopedEntityIds],
    );
    const accountTargetEntityIds = useMemo(
        () =>
            getAccountTargetEntityIds({
                conditions: allConditions,
                actions: allActions,
                scopedEntityIds,
            }),
        [allConditions, allActions, scopedEntityIds],
    );
    const entityIdsWithAccrualLedger = useEntityIdsWithAccrualLedger(
        accountTargetEntityIds,
    );
    const actionScopedEntityIds =
        accountTargetEntityIds.length > 0 ? accountTargetEntityIds : undefined;
    const requiredScheduleEntityIds = useMemo(
        () =>
            getRequiredScheduleEntityIds({
                conditionEntityIds: effectiveEntityIds,
                actions: allActions,
            }),
        [allActions, effectiveEntityIds],
    );
    const hasEntityWithoutAccrualLedger = useMemo(
        () =>
            accountTargetEntityIds.length > 0 &&
            accountTargetEntityIds.some(
                (id) => !entityIdsWithAccrualLedger.has(id),
            ),
        [accountTargetEntityIds, entityIdsWithAccrualLedger],
    );

    const selectedTypes = useMemo(
        () =>
            allActions
                .map((a) => a.actionType)
                .filter((v): v is FormRuleActionType => v !== undefined),
        [allActions],
    );

    const selectedGroup = useMemo(
        () => ruleGroupsList.data?.find((g) => g.id === groupId),
        [ruleGroupsList.data, groupId],
    );

    const actionTypesOptions = useMemo(() => {
        const actions =
            selectedGroup?.type === RuleGroupType.ACCOUNTING
                ? AccountingRuleFormActions
                : BusinessRuleFormActions;

        return actions
            .filter(
                (v) =>
                    v !== FormRuleActionType.CLASS ||
                    (hasClassesAccess && accessFetched) ||
                    selectedTypes.includes(v),
            )
            .filter(
                (v) =>
                    v !== FormRuleActionType.ASSIGN_TO_SCHEDULE ||
                    schedules ||
                    selectedTypes.includes(v),
            )
            .filter((v) => !selectedTypes.includes(v) || v === actionType)
            .filter(
                // GL-first workspaces classify by general ledger, so the
                // category action is never selectable as a new option. Keep it
                // when it's already the action's selected type so pre-existing
                // rules remain visible and editable.
                (v) =>
                    !isGlFirst ||
                    v !== FormRuleActionType.CATEGORY ||
                    v === actionType,
            )
            .map((value) => {
                if (
                    value === FormRuleActionType.ACCRUAL_ACCOUNT_ID &&
                    hasEntityWithoutAccrualLedger
                ) {
                    return {
                        value,
                        label: value,
                        disabled: true,
                        disabledReason:
                            "Some of the selected entities don't have an Accrual ledger",
                    };
                }
                return { value, label: value };
            });
    }, [
        selectedGroup?.type,
        actionType,
        selectedTypes,
        accessFetched,
        hasClassesAccess,
        schedules,
        hasEntityWithoutAccrualLedger,
        isGlFirst,
    ]);

    return (
        <>
            <div className={styles.sectionRowContent}>
                {labelStyle === "plain" ? (
                    <span className={labelClassName}>{actionType}</span>
                ) : (
                    <Picker.Select
                        items={actionTypesOptions}
                        value={actionType ?? null}
                        onValueChange={(value) => {
                            if (value !== null && value !== actionType) {
                                actionTypeHelpers.setValue(value);
                                // Drop any previously-picked value so a stale
                                // account/category/etc from the prior action
                                // type can't leak into the new one (e.g.
                                // accrual account UUID surviving a switch to
                                // "Assign to modified cash ledger").
                                actionValueHelpers.setValue(undefined);
                            }
                        }}
                        placeholder="Select action type"
                        trigger={
                            <Picker.Button
                                variant="primary"
                                size={controlButtonSize ?? "md"}
                            />
                        }
                    />
                )}

                {actionType === FormRuleActionType.CATEGORY && (
                    <CategoryActionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                    />
                )}

                {actionType === FormRuleActionType.ENTITY && (
                    <EntityActionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                    />
                )}

                {actionType === FormRuleActionType.ACCOUNT_ID && (
                    <CashAccountActionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                        scopedEntityIds={actionScopedEntityIds}
                    />
                )}
                {actionType === FormRuleActionType.ACCOUNT_NAME && (
                    <CashAccountActionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                        scopedEntityIds={actionScopedEntityIds}
                    />
                )}
                {actionType === FormRuleActionType.ACCRUAL_ACCOUNT_ID && (
                    <AccrualAccountActionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                        scopedEntityIds={actionScopedEntityIds}
                    />
                )}

                {actionType === FormRuleActionType.MEMO && (
                    <MemoActionForm index={index} />
                )}

                {actionType === FormRuleActionType.COUNTERPARTY && (
                    <CounterpartyActionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                    />
                )}

                {actionType === FormRuleActionType.CUSTOMER && (
                    <CustomerActionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                    />
                )}

                {actionType === FormRuleActionType.CLASS && (
                    <ClassActionForm
                        index={index}
                        controlButtonSize={controlButtonSize}
                    />
                )}

                {actionType === FormRuleActionType.ASSIGN_TO_SCHEDULE && (
                    <ScheduleActionForm
                        index={index}
                        requiredEntityIds={requiredScheduleEntityIds}
                        controlButtonSize={controlButtonSize}
                    />
                )}
            </div>

            {canRemove && (
                <Menu.More>
                    <Menu.Item onClick={() => onRemove(index)} destructive>
                        Remove
                    </Menu.Item>
                </Menu.More>
            )}
        </>
    );
};
